import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui';
import { step4Schema, type Step4Data as FormData } from '@/validations';
import { LinkedInCompanyPageSelector } from '@/features/marketing/components';
import { useLinkedInTokenStatus } from '@/hooks';
import { Linkedin } from 'lucide-react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

interface Step3LinkedInProps {
  initialData?: { linkedinUrl?: string };
  onNext: (data: FormData) => void;
  onBack?: () => void;
  isSubmitting?: boolean;
}

export const Step3LinkedIn: FC<Step3LinkedInProps> = ({
  initialData,
  onNext,
  onBack,
  isSubmitting = false,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(step4Schema),
    defaultValues: {
      linkedinUrl: initialData?.linkedinUrl || '',
    },
  });

  // Check if user has LinkedIn posting access
  const { hasToken: hasLinkedInToken, isLoading: tokenLoading, refetch: refetchTokenStatus } = useLinkedInTokenStatus();
  const [selectedOrganizationUrn, setSelectedOrganizationUrn] = useState<string | undefined>();
  const [selectedCompanyPageUrl, setSelectedCompanyPageUrl] = useState<string | undefined>();
  const [isConnecting, setIsConnecting] = useState(false);
  const [postingConfig, setPostingConfig] = useState<{
    type: 'profile' | 'company' | 'both';
    enabledFeatures: { profilePosting: boolean; companyPosting: boolean };
  } | null>(null);
  const [configLoading, setConfigLoading] = useState(true);

  // Watch the linkedinUrl field value
  const linkedinUrlValue = watch('linkedinUrl');

  // Fetch posting configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get('/mxm/posts/posting-config');
        setPostingConfig(response.data);
      } catch (error) {
        console.error('Failed to fetch LinkedIn posting config:', error);
        // Default to company posting if fetch fails
        setPostingConfig({ type: 'company', enabledFeatures: { profilePosting: false, companyPosting: true } });
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Determine if we should show company page selector
  // Only show if company posting is enabled AND it's not profile-only mode
  const isProfileOnly = postingConfig?.type === 'profile';
  const showCompanyPageSelector =
    postingConfig !== null &&
    postingConfig.enabledFeatures.companyPosting === true &&
    !isProfileOnly;

  // Handle OAuth callback - check if user just returned from LinkedIn connection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('linkedin_posting_success');

    if (success === 'true') {
      // Remove query params from URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Refetch token status to update the UI
      refetchTokenStatus();
    }
  }, [refetchTokenStatus]);

  // If we have a selected URL but form doesn't have it, update the form
  useEffect(() => {
    if (selectedCompanyPageUrl && linkedinUrlValue !== selectedCompanyPageUrl) {
      setValue('linkedinUrl', selectedCompanyPageUrl, { shouldValidate: true });
    }
  }, [selectedCompanyPageUrl, linkedinUrlValue, setValue]);

  // Handle form submission - include the selected company page URL (only if company posting is enabled)
  const handleFormSubmit = async (data: FormData) => {
    // For profile-only posting, linkedinUrl is not needed
    if (isProfileOnly) {
      onNext({ linkedinUrl: '' });
      return;
    }

    // Get the current form value (which should be updated by setValue)
    const currentUrl = watch('linkedinUrl');
    // Use the selected company page URL if available, otherwise use form data
    const linkedinUrl = selectedCompanyPageUrl || currentUrl || data.linkedinUrl || '';

    // If we have a selected organization URN, save it to backend now (only on form submit)
    if (selectedOrganizationUrn && linkedinUrl) {
      try {
        await api.post('/users/linkedin-company-page', { organizationUrn: selectedOrganizationUrn });
      } catch (error: unknown) {
        console.error('Failed to save selected company page:', error);
        // Continue with form submission even if saving URN fails
      }
    }

    onNext({ linkedinUrl });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Header */}
      {onBack && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {isProfileOnly ? 'LinkedIn Profile' : 'LinkedIn Company Page'}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {isProfileOnly
              ? 'Connect your LinkedIn account to enable posting to your personal profile'
              : 'Connect your LinkedIn account and select which company page you want to post to'}
          </p>
        </div>
      )}

      {/* LinkedIn Connection Section */}
      {!tokenLoading && !configLoading && (
        <>
          {!hasLinkedInToken ? (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Linkedin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-900 mb-1">
                    Connect LinkedIn for Posting
                  </h3>
                  <p className="text-xs text-blue-800 mb-3">
                    {isProfileOnly
                      ? 'Connect your LinkedIn account to enable posting to your personal profile.'
                      : 'Connect your LinkedIn account to enable posting to your company pages. After connecting, you\'ll be able to select which company page to post to.'}
                  </p>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={async () => {
                      setIsConnecting(true);
                      try {
                        // Get current pathname to redirect back here after OAuth
                        const currentPath = window.location.pathname;
                        const response = await api.post('/auth/linkedin-connect-posting', {
                          redirectUrl: currentPath,
                        });
                        const { authUrl } = response.data;
                        window.location.href = authUrl;
                      } catch (error: unknown) {
                        console.error('Failed to connect LinkedIn:', error);
                        alert(getErrorMessage(error));
                        setIsConnecting(false);
                      }
                    }}
                    isLoading={isConnecting}
                    className="text-sm"
                  >
                    {isConnecting ? 'Connecting...' : 'Connect LinkedIn'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">
                    LinkedIn Connected
                  </span>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={async () => {
                    if (!confirm('Are you sure you want to disconnect your LinkedIn account? You will need to reconnect to post to LinkedIn.')) {
                      return;
                    }
                    setIsConnecting(true);
                    try {
                      await api.delete('/users/linkedin-disconnect');
                      // Refetch token status to update UI
                      await refetchTokenStatus();
                      // Clear selected organization and URL
                      setSelectedOrganizationUrn(undefined);
                      setSelectedCompanyPageUrl(undefined);
                      setValue('linkedinUrl', '');
                    } catch (error: unknown) {
                      console.error('Failed to disconnect LinkedIn:', error);
                      alert(getErrorMessage(error));
                    } finally {
                      setIsConnecting(false);
                    }
                  }}
                  isLoading={isConnecting}
                  className="text-xs"
                >
                  {isConnecting ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              </div>
              {/* Only show company page selector if company posting is enabled (not profile-only) */}
              {showCompanyPageSelector && (
                <LinkedInCompanyPageSelector
                  selectedUrn={selectedOrganizationUrn}
                  onSelect={(urn, url) => {
                    setSelectedOrganizationUrn(urn);
                    setSelectedCompanyPageUrl(url);
                    if (url) {
                      setValue('linkedinUrl', url, { shouldValidate: true });
                    }
                  }}
                />
              )}
            </div>
          )}
        </>
      )}

      {/* Hidden input to store the selected LinkedIn URL (only if company posting is enabled) */}
      {!isProfileOnly && (
        <input
          type="hidden"
          {...register('linkedinUrl')}
        />
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-6 mt-6 border-t border-slate-200 justify-end">
        {onBack && (
          <Button type="button" variant="secondary" onClick={onBack}>
            Back
          </Button>
        )}
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};
